import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2, TrendingUp, X } from 'lucide-react';
import {
  createEquityTradingService,
  type EquityEnv, type EquityAccount, type EquityPosition,
} from '@/services/equityTradingService';
import { TRADE_PREFILL_KEY } from '@/components/symbol/SymbolDrawer';

/**
 * Manual equity (Alpaca) trading panel — the hands-on order ticket counterpart
 * of ForexPanel. Account + open positions (with close) + a market-order ticket.
 * Env-aware: paper hits /api/PaperTrading, live hits /api/EquityLiveTrading
 * (real money) and requires a two-click confirm. Broker-direct — no managed
 * StrategyTrade is created.
 */
export const EquityManualPanel: React.FC<{ env: EquityEnv }> = ({ env }) => {
  const svc = React.useMemo(() => createEquityTradingService(env), [env]);
  const live = env === 'live';

  const [account, setAccount] = useState<EquityAccount | null>(null);
  const [positions, setPositions] = useState<EquityPosition[]>([]);
  const [notConnected, setNotConnected] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([svc.getAccount(), svc.getPositions()]);
      setAccount(a); setPositions(p ?? []); setNotConnected(null);
    } catch (e: any) {
      setAccount(null); setPositions([]);
      setNotConnected(e?.message || 'not connected');
    }
  }, [svc]);

  useEffect(() => { void refresh(); }, [refresh]);

  return (
    <div className="space-y-4">
      {live && (
        <div className="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/5 p-3 text-xs">
          <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />
          <span className="text-muted-foreground">
            <span className="font-semibold text-red-500">Real money.</span> Orders here execute on your
            funded Alpaca live account. Requires your live kill switch + the master switch to be on.
          </span>
        </div>
      )}

      {/* Two columns from xl up. The panel was capped at max-w-3xl and stacked
          everything in one narrow strip, so on a wide screen the account summary,
          the ticket and the positions table all sat in the left 768px with the rest
          of the page empty. The ticket is a fixed-width form and does not benefit
          from more room; the positions table does, so it takes the free space. */}
      <div className="grid gap-4 xl:grid-cols-[minmax(340px,26rem)_minmax(0,1fr)] xl:items-start">
        <div className="space-y-4">
          <AccountCard account={account} notConnected={notConnected} live={live} />
          {/* No interactive order ticket when the account probe failed — a real-money
              ticket whose every submit can only error is the audit's "unusable ticket". */}
          {!notConnected && <OrderTicket svc={svc} live={live} onPlaced={refresh} />}
        </div>

        {!notConnected && (
          <PositionsCard positions={positions} live={live}
            onClose={async (p) => { await svc.closePosition(p.symbol, p.tradeId); await refresh(); }} />
        )}
      </div>
    </div>
  );
};

const AccountCard: React.FC<{ account: EquityAccount | null; notConnected: string | null; live: boolean }> = ({ account, notConnected, live }) => (
  <Card>
    <CardHeader className="pb-3">
      <CardTitle className="text-base flex items-center gap-2">
        <TrendingUp className="h-4 w-4 text-primary" />
        {live ? 'Live' : 'Paper'} Equity Account
        {account?.accountNumber === 'SIMULATED' && (
          <span className="rounded-full border border-sky-500/40 bg-sky-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-sky-500">
            Simulated
          </span>
        )}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      {account ? (
        <div className="grid grid-cols-3 gap-3 text-sm">
          <Stat label="Equity" value={account.equity} />
          <Stat label="Cash" value={account.cash} />
          <Stat label="Buying power" value={account.buyingPower} />
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">
          {notConnected ? `Not connected — ${notConnected}` : 'Loading…'}
        </div>
      )}
    </CardContent>
  </Card>
);

const Stat: React.FC<{ label: string; value: number | null }> = ({ label, value }) => (
  <div>
    <div className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</div>
    <div className="font-mono">{value == null ? '—' : `$${value.toLocaleString()}`}</div>
  </div>
);

const OrderTicket: React.FC<{ svc: ReturnType<typeof createEquityTradingService>; live: boolean; onPlaced: () => Promise<void> }> = ({ svc, live, onPlaced }) => {
  // One-shot prefill from the symbol drawer's "Paper trade" button. Read-and-clear
  // so a later manual visit starts blank. Paper only — never prefill a LIVE ticket.
  const [symbol, setSymbol] = useState(() => {
    if (live) return '';
    try {
      const s = sessionStorage.getItem(TRADE_PREFILL_KEY);
      if (s) { sessionStorage.removeItem(TRADE_PREFILL_KEY); return s; }
    } catch { /* private mode */ }
    return '';
  });
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  // A real default, not a placeholder: the E2E showed a ticket that LOOKS filled
  // ("10" as placeholder) with a dead button until the user types — a lie of a UI.
  const [qty, setQty] = useState('10');
  const [confirm, setConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  // Synchronous guard: a rapid double-click can re-enter submit() before React
  // re-renders with submitting=true — for real money that would place two orders.
  const inFlight = React.useRef(false);

  const quantity = Number(qty);
  // Symbols are case-SENSITIVE (TpC ≠ TPC) — submit exactly what was typed, trimmed.
  const cleanSymbol = symbol.trim();
  const valid = cleanSymbol.length > 0 && quantity > 0;

  const submit = async () => {
    if (!valid || inFlight.current) return;
    if (live && !confirm) { setConfirm(true); return; }   // two-click for real money
    inFlight.current = true;
    setSubmitting(true); setMsg(null);
    try {
      await svc.placeOrder({ symbol: cleanSymbol, side, quantity });
      setMsg({ ok: true, text: `Order placed: ${side} ${quantity} ${cleanSymbol}` });
      setSymbol(''); setQty(''); setConfirm(false);
      await onPlaced();
    } catch (e: any) {
      setMsg({ ok: false, text: e?.message || 'Order failed' });
      setConfirm(false);   // failed live order must re-confirm on the next click
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };

  const inputCls = 'h-9 rounded-md border bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring';

  return (
    <Card>
      <CardHeader className="pb-3"><CardTitle className="text-base">Order ticket</CardTitle></CardHeader>
      <CardContent className="pt-0 space-y-3">
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-muted-foreground uppercase">Symbol</span>
            <input className={`${inputCls} w-28 font-mono`} value={symbol}
              onChange={(e) => { setSymbol(e.target.value); setConfirm(false); }} placeholder="AAPL" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-muted-foreground uppercase">Side</span>
            <select className={`${inputCls} w-24`} value={side}
              onChange={(e) => { setSide(e.target.value as 'buy' | 'sell'); setConfirm(false); }}>
              <option value="buy">Buy</option>
              <option value="sell">Sell</option>
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[11px] text-muted-foreground uppercase">Quantity</span>
            <input className={`${inputCls} w-28 font-mono`} type="number" min="0" value={qty}
              onChange={(e) => { setQty(e.target.value); setConfirm(false); }} placeholder="10" />
          </label>
          <Button size="sm" disabled={!valid || submitting}
            variant={live ? (confirm ? 'destructive' : 'default') : 'default'}
            onClick={() => void submit()}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            {live ? (confirm ? 'Confirm REAL order' : `Place live ${side}`) : `Place ${side} (market)`}
          </Button>
          {confirm && !submitting && (
            <Button size="sm" variant="outline" onClick={() => setConfirm(false)}>Cancel</Button>
          )}
        </div>
        {msg && (
          <div className={`text-xs flex items-center gap-1 ${msg.ok ? 'text-emerald-500' : 'text-red-500'}`}>
            {!msg.ok && <AlertTriangle className="h-3 w-3" />} {msg.text}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PositionsCard: React.FC<{ positions: EquityPosition[]; live: boolean; onClose: (p: EquityPosition) => Promise<void> }> = ({ positions, live, onClose }) => {
  const [closing, setClosing] = useState<string | null>(null);
  // Live close flattens a real-money position — require a per-row confirm click.
  const [confirmSym, setConfirmSym] = useState<string | null>(null);
  // The full row goes through, not just the symbol: an automation row carries a
  // tradeId and must close by trade, or a manual position on the same symbol
  // would be flattened in its place.
  const handleClick = async (p: EquityPosition) => {
    if (live && confirmSym !== p.symbol) { setConfirmSym(p.symbol); return; }
    setConfirmSym(null);
    setClosing(p.symbol);
    try { await onClose(p); } finally { setClosing(null); }
  };
  return (
    <Card>
      <CardHeader className="pb-3"><CardTitle className="text-base">Open positions</CardTitle></CardHeader>
      <CardContent className="pt-0">
        {positions.length === 0 ? (
          <div className="text-xs text-muted-foreground">No open positions.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[11px] text-muted-foreground uppercase">
                <th className="text-left font-medium py-1">Symbol</th>
                <th className="text-right font-medium">Qty</th>
                <th className="text-right font-medium">Avg</th>
                <th className="text-right font-medium">Last</th>
                <th className="text-right font-medium">P&L</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {positions.map((p) => (
                <tr key={p.tradeId ?? p.symbol /* manual + automation rows can share a symbol */} className="border-t">
                  <td className="py-1.5 font-mono">{p.symbol}</td>
                  <td className="text-right font-mono">{p.quantity}</td>
                  <td className="text-right font-mono">{p.averageEntryPrice}</td>
                  <td className="text-right font-mono">{p.currentPrice}</td>
                  <td className={`text-right font-mono ${p.unrealizedProfitLoss >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                    {p.unrealizedProfitLoss?.toFixed?.(2) ?? p.unrealizedProfitLoss}
                  </td>
                  <td className="text-right">
                    <Button size="sm" disabled={closing === p.symbol}
                      variant={confirmSym === p.symbol ? 'destructive' : 'ghost'}
                      onClick={() => void handleClick(p)}>
                      {closing === p.symbol
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : confirmSym === p.symbol ? <span className="text-xs px-1">Confirm close</span> : <X className="h-3.5 w-3.5" />}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EquityManualPanel;
